import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useSupplierAddUserMutation,
  useSupplierRemoveUserMutation,
  useUserRemoveRoleMutation,
} from '../../../generated';
import { SupplierSelectOption } from '../components/SupplierLinking/utils';

export interface UseSupplierLinkingResponse {
  linkUserToSupplier: (
    supplier: SupplierSelectOption,
    userId: string
  ) => Promise<void>;
  unlinkUserFromSupplier: (userId: string, roles?: string[]) => Promise<void>;
  isLinking: boolean;
  isUnlinking: boolean;
}

export const useSupplierLinking = (): UseSupplierLinkingResponse => {
  const { addNotification } = useNotification();

  const [supplierAddUser, { loading: isLinking }] =
    useSupplierAddUserMutation();
  const [supplierRemoveUser, { loading: isUnlinking }] =
    useSupplierRemoveUserMutation();
  const [userRemoveRole] = useUserRemoveRoleMutation();

  const linkUserToSupplier = useCallback(
    async (supplier: SupplierSelectOption, userId: string) => {
      try {
        await supplierAddUser({
          variables: {
            userId,
            supplierId: supplier.value,
            withRoles: true,
          },
        });

        addNotification({
          type: 'success',
          title: 'User Linked',
          message: `Successfully linked user to ${supplier.label}`,
        });

        // Refresh the page to update the user data
        window.location.reload();
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Link Failed',
          message: 'Failed to link user to supplier. Please try again.',
        });
        throw error;
      }
    },
    [supplierAddUser, addNotification]
  );

  const unlinkUserFromSupplier = useCallback(
    async (userId: string, roles?: string[]) => {
      try {
        // First remove any roles if they exist
        if (roles && roles.length > 0) {
          const firstRole = roles[0];
          await userRemoveRole({
            variables: {
              userId,
              role: firstRole as any, // Role enum type
            },
          });
        }

        // Then remove the supplier link
        await supplierRemoveUser({
          variables: {
            userId,
            withRoles: true,
          },
        });

        addNotification({
          type: 'success',
          title: 'User Unlinked',
          message: 'Successfully unlinked user from supplier',
        });

        // Refresh the page to update the user data
        window.location.reload();
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Unlink Failed',
          message: 'Failed to unlink user from supplier. Please try again.',
        });
        throw error;
      }
    },
    [supplierRemoveUser, userRemoveRole, addNotification]
  );

  return {
    linkUserToSupplier,
    unlinkUserFromSupplier,
    isLinking,
    isUnlinking,
  };
};
